import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  ChevronLeft,
  Volume2,
  VolumeX,
  Monitor,
  Copy,
  Home,
  Edit2,
  Check,
  X,
  Pencil,
  Users,
  Settings,
  Crown,
  UserCheck,
  Presentation,
  Share2,
  HelpCircle,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { saveRecentGame } from "./Home";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0b01affe-3e55-4e38-af85-fb6ae72b034b/artifacts/aji67e46_CAKE%20FACTORY_00_LOGO_600px.png";

const ROUND_NAMES = ["Test", "Round 1", "Round 2", "Round 3", "Round 4"];

const PENALTIES = [20, 50];

// Cake scoring:
// 1 cake = 50 (fixed)
// 2 cakes = 150 (fixed)
// 3 cakes = 250 first, then +50 each press
// 4 cakes = 400 first, then +50 each press
const CAKE_BASE_SCORES = {
  1: 50,
  2: 150,
  3: 250,
  4: 400,
};

// Permanent upgrades that stay on once activated until end of game
const PERMANENT_UPGRADES = ["Double Tray", "Extra Layers", "Cake Storage", "Swap Tables", "4th Order Slot", "4th Delivery Slot"];

const UPGRADES = [
  { name: "Strategy Break", price: 70, short: "Strategy" },
  { name: "Birthday Candle", price: 75, short: "Candle" },
  { name: "Double Tray", price: 80, short: "Dbl Tray" },
  { name: "Extra Layers", price: 85, short: "Layers" },
  { name: "Cake Storage", price: 120, short: "Storage" },
  { name: "Swap Tables", price: 150, short: "Swap" },
  { name: "Last Call", price: 200, short: "Last Call" },
  { name: "4th Order Slot", price: 250, short: "4th Order" },
  { name: "4th Delivery Slot", price: 250, short: "4th Deliv" },
];

export default function GameMaster() {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  
  // Multi-GM mode state
  const [isLeadGM, setIsLeadGM] = useState(() => {
    const saved = localStorage.getItem(`gm-lead-${gameId}`);
    return saved === null ? true : saved === 'true';
  });
  const [myTeamIds, setMyTeamIds] = useState(() => {
    const saved = localStorage.getItem(`gm-teams-${gameId}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(420);
  const [timerRunning, setTimerRunning] = useState(false);
  // Staff Meeting state
  const [meetingActive, setMeetingActive] = useState(false);
  const [meetingTimerSeconds, setMeetingTimerSeconds] = useState(600);
  const [meetingTimerRunning, setMeetingTimerRunning] = useState(false);
  
  // Cake press counts for incremental scoring (3-cake and 4-cake)
  // Structure: { [teamId]: { 3: count, 4: count }, ... }
  const [cakePressCount, setCakePressCount] = useState({});
  const [lastRoundForCakes, setLastRoundForCakes] = useState(null);
  
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [editScoreDialog, setEditScoreDialog] = useState(false);
  const [editScoreValue, setEditScoreValue] = useState("");
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showGuideDialog, setShowGuideDialog] = useState(false);

  const timerRef = useRef(null);
  const meetingTimerRef = useRef(null);
  const audioRef = useRef(null);
  const lastFetchRef = useRef(0);

  // Format timer as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Fetch game data
  const fetchGame = useCallback(async (showError = true) => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`);
      const gameData = response.data;
      
      // Always update game state first
      setGame(gameData);
      
      // Sync meeting state from backend
      setMeetingActive(gameData.meeting_active || false);
      
      if (!isLeadGM) {
        // Non-lead GMs always follow backend state for ALL timers
        setMeetingTimerRunning(gameData.meeting_timer_running || false);
        setMeetingTimerSeconds(gameData.meeting_timer_seconds ?? 600);
        setTimerRunning(gameData.timer_running || false);
        setTimerSeconds(gameData.timer_seconds ?? 420);
      }
      
      if (loading) setLoading(false);
    } catch (error) {
      if (showError) {
        toast.error("Game not found");
        navigate("/");
      }
    }
  }, [gameId, navigate, loading, isLeadGM]);

  // Initial fetch
  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  // Save game to "Recent Games" once loaded
  useEffect(() => {
    if (game) {
      saveRecentGame({
        id: game.id || gameId,
        num_teams: game.teams?.length ?? null,
        team_names: game.teams?.map((t) => t.name) ?? [],
        created: false,
      });
    }
  }, [game, gameId]);

  // Polling for updates - faster interval for meeting sync
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastFetchRef.current > 1000) {
        fetchGame(false);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [fetchGame]);

  // Sync timer to backend
  const syncTimerToBackend = useCallback(async (running, seconds) => {
    try {
      await axios.put(`${API}/games/${gameId}/timer`, {
        timer_running: running,
        timer_seconds: seconds,
      });
    } catch (error) {
      console.error("Failed to sync timer", error);
    }
  }, [gameId]);

  // Sync meeting timer to backend
  const syncMeetingTimerToBackend = useCallback(async (active, running, seconds) => {
    try {
      await axios.put(`${API}/games/${gameId}/meeting-timer`, {
        meeting_active: active,
        meeting_timer_running: running,
        meeting_timer_seconds: seconds,
      });
    } catch (error) {
      console.error("Failed to sync meeting timer", error);
    }
  }, [gameId]);

  // Timer logic - Only Lead GM runs the countdown
  useEffect(() => {
    if (!isLeadGM) return; // Non-lead GMs don't run local countdown
    
    if (timerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => {
          const newTime = prev - 1;
          // Sync to backend every second for real-time display sync
          syncTimerToBackend(newTime > 0, newTime);
          if (newTime <= 0) {
            setTimerRunning(false);
            // Play alarm
            if (soundEnabled && audioRef.current) {
              audioRef.current.play().catch(() => {});
            }
            toast.success("Time's up!", { duration: 5000 });
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerRunning, soundEnabled, syncTimerToBackend, isLeadGM]);

  // Meeting timer logic - Only Lead GM runs the countdown
  useEffect(() => {
    if (!isLeadGM) return; // Non-lead GMs don't run local countdown
    
    if (meetingTimerRunning && meetingTimerSeconds > 0) {
      meetingTimerRef.current = setInterval(() => {
        setMeetingTimerSeconds((prev) => {
          const newTime = prev - 1;
          // Sync to backend every second
          syncMeetingTimerToBackend(meetingActive, newTime > 0, newTime);
          if (newTime <= 0) {
            setMeetingTimerRunning(false);
            // Play alarm
            if (soundEnabled && audioRef.current) {
              audioRef.current.play().catch(() => {});
            }
            toast.success("Staff Meeting time's up!", { duration: 5000 });
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (meetingTimerRef.current) {
        clearInterval(meetingTimerRef.current);
      }
    }
    return () => {
      if (meetingTimerRef.current) {
        clearInterval(meetingTimerRef.current);
      }
    };
  }, [meetingTimerRunning, meetingActive, soundEnabled, syncMeetingTimerToBackend, isLeadGM]);

  // Get current selected team from filtered list
  const getSelectedTeam = useCallback(() => {
    if (!game) return null;
    const filteredTeams = myTeamIds.length === 0 
      ? game.teams 
      : game.teams.filter(t => myTeamIds.includes(t.id));
    return filteredTeams[selectedTeamIndex] || filteredTeams[0];
  }, [game, myTeamIds, selectedTeamIndex]);

  // Reset cake counts when round changes
  useEffect(() => {
    if (game && game.current_round !== lastRoundForCakes) {
      setCakePressCount({});
      setLastRoundForCakes(game.current_round);
    }
  }, [game, lastRoundForCakes]);

  // Handle cake sale with incremental scoring
  const handleCakeSale = async (cakeCount) => {
    const team = getSelectedTeam();
    if (!team) return;
    
    let amount;
    const teamId = team.id;
    
    if (cakeCount <= 2) {
      // Fixed amount for 1 and 2 cakes
      amount = CAKE_BASE_SCORES[cakeCount];
    } else {
      // Incremental for 3 and 4 cakes
      const currentCount = cakePressCount[teamId]?.[cakeCount] || 0;
      amount = CAKE_BASE_SCORES[cakeCount] + (currentCount * 50);
      
      // Update press count
      setCakePressCount(prev => ({
        ...prev,
        [teamId]: {
          ...prev[teamId],
          [cakeCount]: currentCount + 1
        }
      }));
    }
    
    await updateScore(amount);
  };

  // Get display amount for cake button
  const getCakeDisplayAmount = (cakeCount) => {
    const team = getSelectedTeam();
    if (!team) return CAKE_BASE_SCORES[cakeCount];
    
    if (cakeCount <= 2) {
      return CAKE_BASE_SCORES[cakeCount];
    }
    
    const currentCount = cakePressCount[team.id]?.[cakeCount] || 0;
    return CAKE_BASE_SCORES[cakeCount] + (currentCount * 50);
  };

  // Update score
  const updateScore = async (amount) => {
    const team = getSelectedTeam();
    if (!team) return;
    lastFetchRef.current = Date.now();
    
    try {
      await axios.put(`${API}/games/${gameId}/score`, {
        team_id: team.id,
        amount: amount,
      });
      fetchGame(false);
    } catch (error) {
      toast.error("Failed to update score");
    }
  };

  // Set score manually
  const setScoreManually = async () => {
    const team = getSelectedTeam();
    if (!team) return;
    const score = parseInt(editScoreValue, 10);
    if (isNaN(score)) {
      toast.error("Please enter a valid number");
      return;
    }
    lastFetchRef.current = Date.now();
    
    try {
      await axios.put(`${API}/games/${gameId}/set-score`, {
        team_id: team.id,
        score: score,
      });
      setEditScoreDialog(false);
      setEditScoreValue("");
      fetchGame(false);
      toast.success("Score updated");
    } catch (error) {
      toast.error("Failed to set score");
    }
  };

  // Toggle upgrade
  const toggleUpgrade = async (upgradeName) => {
    const team = getSelectedTeam();
    if (!team) return;
    lastFetchRef.current = Date.now();
    
    try {
      await axios.put(`${API}/games/${gameId}/upgrade`, {
        team_id: team.id,
        upgrade_name: upgradeName,
      });
      fetchGame(false);
    } catch (error) {
      // Show specific error message
      const message = error.response?.data?.detail || "Failed to update upgrade";
      toast.error(message);
    }
  };

  // Next round
  const nextRound = async () => {
    if (!game) return;
    lastFetchRef.current = Date.now();
    
    try {
      const response = await axios.put(`${API}/games/${gameId}/next-round`);
      setTimerSeconds(420);
      setTimerRunning(false);
      
      // Show special message when transitioning from Test to Round 1
      if (game.current_round === 0) {
        toast.success("Test round complete! Starting fresh for the game!", { duration: 3000 });
      } else {
        toast.success(`Starting ${ROUND_NAMES[Math.min(game.current_round + 1, 4)]}`);
      }
      
      fetchGame(false);
    } catch (error) {
      toast.error("Failed to advance round");
    }
  };

  // Previous round
  const prevRound = async () => {
    if (!game || game.current_round === 0) return;
    lastFetchRef.current = Date.now();
    
    try {
      await axios.put(`${API}/games/${gameId}/prev-round`);
      setTimerSeconds(420);
      setTimerRunning(false);
      fetchGame(false);
      toast.success(`Back to ${ROUND_NAMES[Math.max(game.current_round - 1, 0)]}`);
    } catch (error) {
      toast.error("Failed to go back");
    }
  };

  // Reset timer
  const resetTimer = async () => {
    setTimerSeconds(420);
    setTimerRunning(false);
    await syncTimerToBackend(false, 420);
  };

  // Start/Stop timer with sync
  const toggleTimer = async () => {
    const newRunning = !timerRunning;
    setTimerRunning(newRunning);
    await syncTimerToBackend(newRunning, timerSeconds);
  };

  // Toggle Staff Meeting mode
  const toggleMeeting = async () => {
    const newActive = !meetingActive;
    setMeetingActive(newActive);
    if (newActive) {
      // Starting meeting - reset meeting timer to 10 min
      setMeetingTimerSeconds(600);
      setMeetingTimerRunning(false);
      toast.success("Staff Meeting started - Upgrades enabled!", { duration: 3000 });
    } else {
      // Ending meeting
      setMeetingTimerRunning(false);
      toast.info("Staff Meeting ended - Upgrades disabled", { duration: 3000 });
    }
    await syncMeetingTimerToBackend(newActive, false, 600);
  };

  // Toggle meeting timer
  const toggleMeetingTimer = async () => {
    const newRunning = !meetingTimerRunning;
    setMeetingTimerRunning(newRunning);
    await syncMeetingTimerToBackend(meetingActive, newRunning, meetingTimerSeconds);
  };

  // Reset meeting timer
  const resetMeetingTimer = async () => {
    setMeetingTimerSeconds(600);
    setMeetingTimerRunning(false);
    await syncMeetingTimerToBackend(meetingActive, false, 600);
  };

  // Save GM settings to localStorage
  useEffect(() => {
    localStorage.setItem(`gm-lead-${gameId}`, isLeadGM.toString());
  }, [isLeadGM, gameId]);

  useEffect(() => {
    localStorage.setItem(`gm-teams-${gameId}`, JSON.stringify(myTeamIds));
  }, [myTeamIds, gameId]);

  // Toggle team in my teams list
  const toggleMyTeam = (teamId) => {
    setMyTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  // Select all teams
  const selectAllTeams = () => {
    if (game) {
      setMyTeamIds(game.teams.map(t => t.id));
    }
  };

  // Clear team selection
  const clearTeamSelection = () => {
    setMyTeamIds([]);
  };

  // Get filtered teams based on selection
  const getMyTeams = () => {
    if (!game) return [];
    if (myTeamIds.length === 0) return game.teams; // Show all if none selected
    return game.teams.filter(t => myTeamIds.includes(t.id));
  };

  // Update team name
  const updateTeamName = async (teamId) => {
    if (!newName.trim()) {
      setEditingName(null);
      return;
    }
    lastFetchRef.current = Date.now();
    
    try {
      await axios.put(`${API}/games/${gameId}/team-name`, {
        team_id: teamId,
        name: newName.trim(),
      });
      setEditingName(null);
      setNewName("");
      fetchGame(false);
    } catch (error) {
      toast.error("Failed to update name");
    }
  };

  // Open display
  const openDisplay = () => {
    window.open(`/display/${gameId}`, "_blank");
  };

  if (loading || !game) {
    return (
      <div className="min-h-screen bg-[#FDF8F6] flex items-center justify-center">
        <div className="text-center">
          <img src={LOGO_URL} alt="Cake Factory" className="w-24 h-24 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600 font-medium">Loading game...</p>
        </div>
      </div>
    );
  }

  // Get filtered teams for this GM
  const myTeams = getMyTeams();
  const selectedTeam = myTeams[selectedTeamIndex] || myTeams[0];
  const hasTeamFilter = myTeamIds.length > 0 && myTeamIds.length < game.teams.length;

  return (
    <div className="min-h-screen bg-[#FDF8F6] flex flex-col">
      {/* Audio element for alarm */}
      <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        preload="auto"
      />

      {/* GM Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Game Master Setup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Lead GM Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Crown className={`w-6 h-6 ${isLeadGM ? "text-[#F59E0B]" : "text-gray-400"}`} />
                <div>
                  <div className="font-semibold">Lead Game Master</div>
                  <div className="text-sm text-gray-500">Control timer & rounds</div>
                </div>
              </div>
              <Switch
                checked={isLeadGM}
                onCheckedChange={setIsLeadGM}
                data-testid="lead-gm-toggle"
              />
            </div>

            {/* Team Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-[#D946EF]" />
                  My Teams
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllTeams}
                    className="text-xs text-[#D946EF] hover:underline"
                  >
                    All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={clearTeamSelection}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {game.teams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => toggleMyTeam(team.id)}
                    className={`p-3 rounded-xl text-left transition-all ${
                      myTeamIds.includes(team.id)
                        ? "bg-[#D946EF] text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`select-team-${team.id}`}
                  >
                    <div className="font-semibold text-sm">{team.name}</div>
                    <div className="text-xs opacity-70">€{team.total_score}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {myTeamIds.length === 0 
                  ? "No filter - showing all teams" 
                  : `Managing ${myTeamIds.length} of ${game.teams.length} teams`}
              </p>
            </div>

            <button
              onClick={() => setShowSetupDialog(false)}
              className="arcade-btn arcade-btn-primary w-full py-3"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Score Edit Dialog */}
      <Dialog open={editScoreDialog} onOpenChange={setEditScoreDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Score - {ROUND_NAMES[game.current_round]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">Current: €{selectedTeam?.scores[game.current_round]}</label>
              <Input
                type="number"
                placeholder="Enter new score"
                value={editScoreValue}
                onChange={(e) => setEditScoreValue(e.target.value)}
                className="mt-2"
                autoFocus
                data-testid="manual-score-input"
              />
            </div>
            <button
              onClick={setScoreManually}
              className="arcade-btn arcade-btn-primary w-full py-3"
              data-testid="save-manual-score-btn"
            >
              Save Score
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share / Invite Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#D946EF] shrink-0" />
              <span>Share this game</span>
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const origin = typeof window !== "undefined" ? window.location.origin : "";
            const gmUrl = `${origin}/game-master/${gameId}`;
            const tvUrl = `${origin}/display/${gameId}`;
            const presUrl = `${origin}/presentation?gameId=${gameId}`;
            const copy = (text, label) => {
              navigator.clipboard.writeText(text);
              toast.success(`${label} copied!`);
            };
            const LinkRow = ({ url, label, Icon, iconColor, bg, testid }) => (
              <button
                onClick={() => copy(url, label)}
                className={`w-full flex items-start gap-2 ${bg} border border-gray-100 rounded-xl px-3 py-2.5 transition-colors overflow-hidden`}
                data-testid={testid}
              >
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${iconColor} shrink-0`} /> {label}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5 break-all leading-snug">{url}</div>
                </div>
                <Copy className="w-4 h-4 text-gray-500 shrink-0 mt-1" />
              </button>
            );
            return (
              <div className="space-y-3 min-w-0">
                <div className="flex flex-col items-center bg-white rounded-2xl p-3 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Scan to join as Game Master</div>
                  <div className="bg-white rounded-lg" data-testid="share-qr-code">
                    <QRCodeCanvas value={gmUrl} size={160} level="M" includeMargin={false} />
                  </div>
                </div>

                <div className="min-w-0">
                  <label className="text-xs font-semibold text-gray-600">Full Game ID</label>
                  <div className="flex items-center gap-2 mt-1 min-w-0">
                    <code className="flex-1 min-w-0 text-[11px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-2 break-all font-mono" data-testid="full-game-id">
                      {gameId}
                    </code>
                    <button
                      onClick={() => copy(gameId, "Game ID")}
                      className="p-2.5 bg-[#A21CAF] text-white rounded-lg hover:bg-[#D946EF] shrink-0"
                      data-testid="copy-full-id-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 min-w-0">
                  <LinkRow url={gmUrl} label="Game Master link" Icon={Crown} iconColor="text-[#F59E0B]" bg="bg-[#FDF8F6] hover:bg-[#FBE4FF]" testid="copy-gm-link-btn" />
                  <LinkRow url={tvUrl} label="TV Display link" Icon={Monitor} iconColor="text-[#22C55E]" bg="bg-[#FDF8F6] hover:bg-[#E0FBE8]" testid="copy-tv-link-btn" />
                  <LinkRow url={presUrl} label="Presentation link" Icon={Presentation} iconColor="text-[#A21CAF]" bg="bg-[#FDF8F6] hover:bg-[#FFF6E0]" testid="copy-pres-link-btn" />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-relaxed">
                  <b>Tip:</b> Send the Game Master link to another referee. After they open it, they tap the gear icon, turn <b>Lead Game Master OFF</b>, and select the teams they manage.
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Guide Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#A21CAF]" />
              Referee Guide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">Roles</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><b>Lead GM</b> — controls round timer, round changes, and Staff Meeting toggle.</li>
                <li><b>Assistant GM</b> — only scores their assigned teams; mirrors timers from the Lead GM.</li>
              </ul>
            </section>
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">Add an Assistant Referee</h4>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>Tap the <b>share icon</b> at the top.</li>
                <li>Show the QR code on this phone, or copy the <b>Game Master link</b> and send it.</li>
                <li>Assistant opens the link, taps the <b>gear icon</b>, turns <b>Lead Game Master OFF</b>.</li>
                <li>Assistant selects the teams they will manage. Done.</li>
              </ol>
            </section>
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">Project the Scoreboard</h4>
              <p>Tap the <b>monitor icon</b> in the header to open the TV view in a new tab — then tap the fullscreen icon there for clean projection.</p>
            </section>
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">Scoring</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>1 cake = €50 · 2 cakes = €150 · 3 cakes = €250 (+€50/extra) · 4 cakes = €400 (+€50/extra)</li>
                <li>Penalties: −€20, −€50</li>
                <li>Upgrades €70–€250 (locked permanently except Strategy/Candle/Last Call)</li>
                <li>Upgrade purchase blocked if it would push score below €0.</li>
              </ul>
            </section>
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">Lost the Game ID?</h4>
              <p>It’s saved on each device under <b>Recent Games</b> on the home screen. The share dialog also shows the full ID, QR code, and shareable links any time.</p>
            </section>
            <button
              onClick={() => setShowGuideDialog(false)}
              className="arcade-btn arcade-btn-primary w-full py-3"
              data-testid="close-guide-btn"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg shadow-md px-4 py-3">
        {/* GM Mode indicator */}
        {hasTeamFilter && (
          <div className="flex items-center justify-center gap-2 mb-2 text-xs">
            <span className="bg-[#D946EF]/10 text-[#D946EF] px-3 py-1 rounded-full font-medium">
              Managing {myTeamIds.length} of {game.teams.length} teams
            </span>
            {isLeadGM && (
              <span className="bg-[#F59E0B]/10 text-[#F59E0B] px-3 py-1 rounded-full font-medium flex items-center gap-1">
                <Crown className="w-3 h-3" /> Lead GM
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10" />
            <div>
              <div className="font-heading text-lg text-[#A21CAF] leading-tight">
                {meetingActive ? "Staff Meeting" : ROUND_NAMES[game.current_round]}
              </div>
              <button
                onClick={() => setShowShareDialog(true)}
                className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D946EF]"
                data-testid="open-share-dialog-btn"
                title="Share game / get QR"
              >
                ID: {gameId.slice(0, 8)}…
                <Share2 className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Timer - Show for all GMs */}
          <div className="text-center">
            <div
              className={`font-mono text-4xl font-bold ${
                meetingActive
                  ? meetingTimerSeconds < 60 ? "timer-warning text-red-500" : "text-[#22C55E]"
                  : timerSeconds < 60 ? "timer-warning text-red-500" : "text-[#0F172A]"
              }`}
              data-testid="timer-display"
            >
              {meetingActive ? formatTime(meetingTimerSeconds) : formatTime(timerSeconds)}
            </div>
            {meetingActive && (
              <div className="text-xs text-[#22C55E] font-semibold">MEETING</div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGuideDialog(true)}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="open-guide-btn"
              title="Referee guide"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowShareDialog(true)}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="share-btn"
              title="Share game"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowSetupDialog(true)}
              className={`p-2 hover:text-[#D946EF] ${hasTeamFilter ? "text-[#D946EF]" : "text-gray-500"}`}
              data-testid="gm-setup-btn"
              title="GM Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={() => window.open(`/presentation?gameId=${gameId}`, "_blank")}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="presentation-btn"
              title="Show Presentation"
            >
              <Presentation className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="sound-toggle-btn"
              title={soundEnabled ? "Mute" : "Unmute"}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button
              onClick={openDisplay}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="open-display-btn"
              title="Open TV Display"
            >
              <Monitor className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate("/")}
              className="p-2 text-gray-500 hover:text-[#D946EF]"
              data-testid="home-btn"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Timer Controls - Only show for Lead GM - Icons only in single row */}
        {isLeadGM && (
          <div className="flex items-center justify-center gap-2">
            {meetingActive ? (
              <>
                <button
                  onClick={toggleMeetingTimer}
                  className={`arcade-btn ${meetingTimerRunning ? "arcade-btn-penalty" : "arcade-btn-sale"} w-12 h-12 flex items-center justify-center`}
                  data-testid="meeting-timer-toggle-btn"
                  title={meetingTimerRunning ? "Pause" : "Start"}
                >
                  {meetingTimerRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                <button
                  onClick={resetMeetingTimer}
                  className="arcade-btn arcade-btn-primary w-12 h-12 flex items-center justify-center"
                  data-testid="meeting-timer-reset-btn"
                  title="Reset Timer"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
                <button
                  onClick={toggleMeeting}
                  className="arcade-btn arcade-btn-penalty w-12 h-12 flex items-center justify-center"
                  data-testid="end-meeting-btn"
                  title="End Meeting"
                >
                  <X className="w-6 h-6" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={prevRound}
                  disabled={game.current_round === 0}
                  className="arcade-btn arcade-btn-primary w-12 h-12 flex items-center justify-center disabled:opacity-50"
                  data-testid="prev-round-btn"
                  title="Previous Round"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={toggleTimer}
                  className={`arcade-btn ${timerRunning ? "arcade-btn-penalty" : "arcade-btn-sale"} w-12 h-12 flex items-center justify-center`}
                  data-testid="timer-toggle-btn"
                  title={timerRunning ? "Pause" : "Start"}
                >
                  {timerRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                <button
                  onClick={resetTimer}
                  className="arcade-btn arcade-btn-primary w-12 h-12 flex items-center justify-center"
                  data-testid="timer-reset-btn"
                  title="Reset Timer"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
                <button
                  onClick={nextRound}
                  disabled={game.current_round >= 4}
                  className="arcade-btn arcade-btn-primary w-12 h-12 flex items-center justify-center disabled:opacity-50"
                  data-testid="next-round-btn"
                  title="Next Round"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <button
                  onClick={toggleMeeting}
                  className="arcade-btn arcade-btn-upgrade w-12 h-12 flex items-center justify-center"
                  data-testid="start-meeting-btn"
                  title="Staff Meeting"
                >
                  <Users className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Meeting status banner for non-Lead GMs */}
        {!isLeadGM && meetingActive && (
          <div className="flex items-center justify-center gap-2 py-2 bg-[#22C55E] text-white">
            <Users className="w-5 h-5" />
            <span className="font-semibold">STAFF MEETING IN PROGRESS</span>
            <span className="font-mono">{formatTime(meetingTimerSeconds)}</span>
          </div>
        )}
      </header>

      {/* Team Selector */}
      <div className="overflow-x-auto hide-scrollbar px-4 py-3 bg-white/50">
        <div className="flex gap-3 min-w-max">
          {myTeams.map((team, index) => (
            <button
              key={team.id}
              onClick={() => setSelectedTeamIndex(index)}
              className={`px-4 py-3 rounded-2xl font-heading text-sm transition-all ${
                index === selectedTeamIndex
                  ? "bg-[#D946EF] text-white scale-105 shadow-lg"
                  : "bg-white text-gray-700 shadow hover:bg-gray-50"
              }`}
              data-testid={`team-selector-${index}`}
            >
              <div className="font-semibold">{team.name}</div>
              <div className="font-mono text-lg">€{team.total_score}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Control Area */}
      <main className="flex-1 overflow-y-auto px-4 pb-6">
        {/* Selected Team Info */}
        <div className="bg-white rounded-2xl p-4 shadow-md mb-4">
          <div className="flex items-center justify-between mb-2">
            {editingName === selectedTeam.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={selectedTeam.name}
                  className="flex-1"
                  autoFocus
                  data-testid="edit-team-name-input"
                />
                <button
                  onClick={() => updateTeamName(selectedTeam.id)}
                  className="p-2 text-green-600"
                  data-testid="save-team-name-btn"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setEditingName(null)}
                  className="p-2 text-red-500"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl text-[#0F172A]">{selectedTeam.name}</h2>
                <button
                  onClick={() => {
                    setEditingName(selectedTeam.id);
                    setNewName(selectedTeam.name);
                  }}
                  className="p-2 text-gray-400 hover:text-[#D946EF]"
                  data-testid="edit-name-btn"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-mono font-bold text-3xl text-[#D946EF]" data-testid="team-total-score">
              €{selectedTeam.total_score}
            </span>
            <span className="text-gray-500 flex items-center gap-1">
              This round: €{selectedTeam.scores[game.current_round]}
              <button
                onClick={() => {
                  setEditScoreValue(selectedTeam.scores[game.current_round].toString());
                  setEditScoreDialog(true);
                }}
                className="p-1 text-gray-400 hover:text-[#D946EF]"
                data-testid="edit-round-score-btn"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </span>
          </div>
        </div>

        {/* Sales Section */}
        <div className={`mb-4 ${meetingActive ? "opacity-50" : ""}`}>
          <h3 className="font-heading text-lg text-gray-700 mb-2 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#EC4899]"></span>
            Sales
            {meetingActive && (
              <span className="text-xs text-gray-400 font-normal ml-2">
                (Disabled during Staff Meeting)
              </span>
            )}
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((cakeCount) => (
              <button
                key={cakeCount}
                onClick={() => handleCakeSale(cakeCount)}
                disabled={meetingActive}
                className={`arcade-btn arcade-btn-sale py-3 flex flex-col items-center gap-1 ${meetingActive ? "cursor-not-allowed" : ""}`}
                data-testid={`cake-btn-${cakeCount}`}
              >
                <div className="text-2xl">
                  {"🎂".repeat(cakeCount)}
                </div>
                <div className="text-sm font-bold">
                  +€{getCakeDisplayAmount(cakeCount)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Penalties Section */}
        <div className={`mb-4 ${meetingActive ? "opacity-50" : ""}`}>
          <h3 className="font-heading text-lg text-gray-700 mb-2 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#EA580C]"></span>
            Penalties
            {meetingActive && (
              <span className="text-xs text-gray-400 font-normal ml-2">
                (Disabled during Staff Meeting)
              </span>
            )}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {PENALTIES.map((amount) => (
              <button
                key={amount}
                onClick={() => updateScore(-amount)}
                disabled={meetingActive}
                className={`arcade-btn arcade-btn-penalty py-4 text-lg font-bold ${meetingActive ? "cursor-not-allowed" : ""}`}
                data-testid={`penalty-btn-${amount}`}
              >
                -€{amount}
              </button>
            ))}
          </div>
        </div>

        {/* Upgrades Section */}
        <div className={`${!meetingActive ? "opacity-50" : ""}`}>
          <h3 className="font-heading text-lg text-gray-700 mb-2 flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${meetingActive ? "bg-[#22C55E]" : "bg-[#F59E0B]"}`}></span>
            Upgrades
            {meetingActive ? (
              <span className="text-xs text-[#22C55E] font-semibold ml-2 bg-green-100 px-2 py-0.5 rounded-full">
                STAFF MEETING - ACTIVE
              </span>
            ) : (
              <span className="text-xs text-gray-400 font-normal ml-2">
                (Start Staff Meeting to enable)
              </span>
            )}
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {UPGRADES.map((upgrade) => {
              const teamUpgrade = selectedTeam.upgrades.find(
                (u) => u.name === upgrade.name
              );
              const isPurchased = teamUpgrade?.purchased;
              const isPermanent = PERMANENT_UPGRADES.includes(upgrade.name);
              const isLockedOn = isPurchased && isPermanent;

              return (
                <button
                  key={upgrade.name}
                  onClick={() => toggleUpgrade(upgrade.name)}
                  disabled={!meetingActive}
                  className={`arcade-btn arcade-btn-upgrade py-3 text-sm ${
                    isPurchased ? "purchased" : ""
                  } ${isLockedOn ? "ring-2 ring-green-400" : ""} ${!meetingActive ? "cursor-not-allowed" : ""}`}
                  data-testid={`upgrade-btn-${upgrade.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="font-semibold flex items-center justify-center gap-1">
                    {isPermanent && <span className="text-xs">🔒</span>}
                    {upgrade.short}
                  </div>
                  <div className="text-xs opacity-80">
                    {isPurchased ? "✓ Bought" : `-€${upgrade.price}`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Round Scores Summary */}
        <div className="mt-6 bg-white rounded-2xl p-4 shadow-md">
          <h3 className="font-heading text-lg text-gray-700 mb-3">Score Breakdown</h3>
          <div className="grid grid-cols-5 gap-2 text-center">
            {ROUND_NAMES.map((name, index) => (
              <div
                key={name}
                className={`p-2 rounded-lg ${
                  index === game.current_round
                    ? "bg-[#D946EF]/10 border-2 border-[#D946EF]"
                    : "bg-gray-50"
                }`}
              >
                <div className="text-xs text-gray-500">{name}</div>
                <div className="font-mono font-bold text-lg" data-testid={`round-score-${index}`}>
                  €{selectedTeam.scores[index]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
